import React, {useEffect, useState} from 'react';
import {
  getAnonymousRequestToken, getLaunchPayload,
} from '../../../state/guest-thunk';
import {useDispatch} from 'react-redux';
import {AppDispatch} from '../../../../shared/state/store';
import {useParams} from 'react-router';
import {paynowService} from '../../../services/paynow-service';
import {
  PatientEncounter,
} from '../../../../patient/components/models/patient-encounter';
import {
  ApplicationStatusState,
  emptyApplicationStatus, GetError,
} from '../../../../security/model/application-status';
import {logUserOut} from '../../../../security/state/user-thunk';
import {GuestToken} from '../../../../security/services/SSO';
import {PaymentApprovalView} from './payment-approval-view';
import HttpInterceptor from '../../../../shared/Http/http-interceptor';
import {patientService} from '../../../../patient/services/patient-service';
import {NoSigAuthDocApprovalPage} from '../no-sig-auth-doc-approval-page';


export interface AuthorizationDocumentStatus {
  authorizationDocumentStatusId: number,
  patientId: number,
  patientChampionId: number | null,
  documentTypeId: number,
  documentStatus: 'created' | 'sent' | 'signed',
  externalSignatureRequestId: string,
  wasPfrAdjustmentSent: null,
  authorizationDocuments: {
    typeOfDoc: 'inst' | 'spa' | 'otp' | 'phi'
    authorizationDocumentsId: number
  }
}

export const PaymentApprovalPage = () => {

  const dispatch = useDispatch<AppDispatch>();
  const params = useParams<{ token?: string }>();

  const [fetchedRecords, setFetchedRecords] = useState<{patientEncounter: PatientEncounter | null, payment: Record<string, any> | null}>(
      {
        patientEncounter: null,
        payment: null
      });
  const [msg, setMsg] = useState<ApplicationStatusState>(
      emptyApplicationStatus);
  const [guestToken, setGuestToken] = useState<GuestToken | null>(null);
  const [authDocStatus, setAuthDocStatus] = useState<AuthorizationDocumentStatus | null>(
      null);
  const [authDocPdfBlob, setAuthDocPdfBlob] = useState<Blob | null>(null)
  const [isAuthDocAlreadyApproved, setIsAuthDocAlreadyApproved] = useState(
      false);
  const [isPageLoading, setIsPageLoading] = useState(true);

  useEffect(() => {

    const getTokenAndPayment = async () => {

      /**
       * A temporary measure to log out user session before loading the page.
       * If a patient portal session exists, it interferes with the loading of all guest pages.
       * */
      await dispatch(logUserOut());

      const tokenResponse = await dispatch(getAnonymousRequestToken()).unwrap()

      setGuestToken(tokenResponse);
      HttpInterceptor(false, tokenResponse, true);

      const launchPayloadResponse: any = await dispatch(getLaunchPayload(({
        'encryptedValue': params.token as string,
        'authToken': tokenResponse?.token,
      }))).unwrap()

      if (launchPayloadResponse?.isProcessingError) {
        throw Error('Error decrypting launch payload');
      }

      const [paymentResponse, patientEncounterResponse] = await Promise.all([
        patientService.getPayment(+launchPayloadResponse?.patientPaymentProgramId, +launchPayloadResponse?.paymentId),
        paynowService.getPatientEncounter(
            +launchPayloadResponse?.patientEncounterId,
            tokenResponse?.token,
            {cacheMode: 'no-cache'}
        )
      ])

      if(paymentResponse.hasErrors) throw Error('Error fetching payment record')

      const selectedAuthDoc = patientEncounterResponse.authorizationDocumentStatus.find(
          (authDoc: AuthorizationDocumentStatus) => {
            return authDoc.authorizationDocuments.typeOfDoc ===
                'otp' && authDoc.documentStatus !== 'signed';
          });

      if (!selectedAuthDoc) {
        setIsAuthDocAlreadyApproved(true);
        return;
      }


      const response = await paynowService.postApi("GET", `/patient-encounter/v2/template/${selectedAuthDoc?.documentTypeId}/export`, null,  tokenResponse?.token, 'application/pdf');
      if(response instanceof ArrayBuffer){
        const blob = new Blob([response], { type: "application/pdf" });
        setAuthDocPdfBlob(blob)
      }

      setFetchedRecords({
        patientEncounter: patientEncounterResponse,
        payment: paymentResponse.entity
      });

      setAuthDocStatus(selectedAuthDoc);
    };



    getTokenAndPayment().then(() => {
      setIsPageLoading(false);
    }).catch((err) => {
      console.error(err)
      setMsg(GetError(GENERIC_ERROR_MSG));
    });

  }, []);

  return (
      <NoSigAuthDocApprovalPage
        isPageLoading={isPageLoading}
        isAuthDocAlreadyApproved={isAuthDocAlreadyApproved}
        areRecordsReady={!!(fetchedRecords.patientEncounter && fetchedRecords.payment)}
        approvalView={
        <PaymentApprovalView
            patientEncounter={fetchedRecords.patientEncounter!}
            payment={fetchedRecords.payment!}
            selectedAuthDocStatus={authDocStatus}
            guestToken={guestToken}
            setMessage={(msg: ApplicationStatusState) => setMsg(msg)}
            authDocPdfBlob={authDocPdfBlob}
        />
      }
        errorMsg={GENERIC_ERROR_MSG}
        msgState={[msg, setMsg]}
        alreadyApprovedText={'This payment has already been approved.'}
      />
  );
};


const GENERIC_ERROR_MSG = 'There is something wrong with your payment. If the problem persists, please reach out to a FinPay administrator at support@finpay.com.';
